import React, { useState, useContext, useEffect, useRef } from "react";
import Modal from "../ui-kit/modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faFileArrowUp } from "@fortawesome/free-solid-svg-icons";
import Tabs from "./tabs";
import classnames from "classnames";
import InlineLoading from "../loader/inline";
import TextInput from "./text";
import "./video.scss";
import { GrigoraDashboardContext } from "../dashboard/context";
import { remove_prefix } from "../helpers";
import { INPUT_LIMITS } from "../input-limits";

const LibraryUI = ({
  src,
  srcMode,
  onSrcChange,
  onSrcModeChange,
  opened,
  setOpened,
  className,
}) => {
  const { getProjectVideos, projectVideos, projectVideosLoaded } = useContext(
    GrigoraDashboardContext,
  );
  const { projectID } = useContext(GrigoraDashboardContext);

  useEffect(() => {
    getProjectVideos()
      .then(() => {})
      .catch(() => {});
  }, []);

  if (!projectVideosLoaded) {
    return <InlineLoading />;
  }
  const classes = classnames(
    "library-container flex flex-wrap h-[43vh] justify-start gap-5 overflow-auto mt-5",
    className,
  );

  return (
    <>
      <div className={classes}>
        {Object.values(projectVideos).map((item) => {
          return (
            <VideoComponent
              id={projectID}
              key={item.name}
              item={item}
              src={src}
              srcMode
              onSrcChange={onSrcChange}
              onSrcModeChange={onSrcModeChange}
              opened
              setOpened={setOpened}
            />
          );
        })}
      </div>
    </>
  );
};

const VideoComponent = (props) => {
  const {
    id,
    item,
    src,
    srcMode,
    onSrcChange,
    onSrcModeChange,
    opened,
    setOpened,
  } = props;

  const { projectID } = useContext(GrigoraDashboardContext);

  var url;
  url = `https://cdn.grigora.co/projects/${id}/${remove_prefix(
    item.sk,
    "video-",
  )}`;

  return (
    <>
      <div
        className={`video-component border border-gray-500 xl:w-[22%] lg:w-[30%] md:w-[45%] w-full min-w-[150px] h-[150px] cursor-pointer ${
          item?.sk === src?.sk ? "active" : ""
        }`}
        onClick={() => {
          onSrcModeChange("cdn");
          onSrcChange({
            ...item,
            projectID: item.projectID ? item.projectID : projectID,
          });
          setOpened(false);
        }}
        title={item.name}
      >
        <video preload="metadata" className="object-cover h-full w-full">
          <source src={url} />
        </video>
      </div>
    </>
  );
};

function VideoPicker({
  src,
  srcMode,
  onSrcChange,
  onSrcModeChange,
  opened,
  setOpened,
}) {
  const { uploadProjectVideo } = useContext(GrigoraDashboardContext) ?? {};

  const { projectID } = useContext(GrigoraDashboardContext) ?? {};

  const handleFileInput = (e) => {
    const url = URL.createObjectURL(e.target.files[0]);
    onSrcChange(url);
    onSrcModeChange("preview");
    setOpened(false);

    if (e.target.files[0]) {
      uploadProjectVideo(e.target.files[0])
        .then((newVideo) => {
          onSrcChange({
            ...newVideo,
            projectID: newVideo.projectID ? newVideo.projectID : projectID,
          });
          onSrcModeChange("cdn");
        })
        .catch((err) => {
          onSrcChange(undefined);
          onSrcModeChange(undefined);
        });
    }
  };

  const inputFileRef = useRef();

  function Upload() {
    return (
      <div className="relative">
        <div className="flex flex-1 items-center justify-center">
          <div
            onClick={() => {
              inputFileRef.current.click();
            }}
            className="border mt-10 cursor-pointer duration-300 text-[#515151] hover:text-white hover:bg-editor-bg-main-hover flex rounded-lg"
          >
            <div className="divide-amber-700  border-r flex items-center justify-center">
              <FontAwesomeIcon icon={faFileArrowUp} className="text-lg p-4" />
            </div>
            <div className="text-center self-center w-full text-sm pl-6 pr-6">
              <span>Upload Files</span>
            </div>
          </div>
          <input
            className="hidden"
            type="file"
            ref={inputFileRef}
            onChange={handleFileInput}
            accept="video/*"
          ></input>
        </div>
      </div>
    );
  }
  function Library() {
    return (
      <LibraryUI
        src={src}
        srcMode={srcMode}
        onSrcChange={onSrcChange}
        onSrcModeChange={onSrcModeChange}
        opened
        setOpened={setOpened}
      />
    );
  }

  const UrlUI = ({
    src,
    srcMode,
    onSrcChange,
    onSrcModeChange,
    opened,
    setOpened,
  }) => {
    const [link, setLink] = useState(srcMode === "link" ? src : "");
    const linkRef = useRef();

    const linkCheck = () => {
      const urlPattern =
        /(?:https?):\/\/(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/;
      if (urlPattern.test(link)) {
        linkRef.current.innerText = "";
        return true;
      } else {
        linkRef.current.innerText = "Not a valid link";
        return false;
      }
    };

    const handleSave = (e) => {
      e.preventDefault();
      if (linkCheck()) {
        onSrcChange(link);
        onSrcModeChange("link");
        setOpened(false);
      }
    };

    return (
      <>
        <div className="mt-4">
          <TextInput
            minLength={INPUT_LIMITS["outbound-link"].minlength}
            maxLength={INPUT_LIMITS["outbound-link"].maxlength}
            label={"URL"}
            value={link}
            onChange={setLink}
          />
          <div className="text-grigora-base text-red-500" ref={linkRef}></div>
          <button
            onClick={handleSave}
            // className="text-sm py-1 mt-2 px-5 bg-blue-700 text-white hover:bg-blue-800"
            className="grigora-action-btn"
          >
            Save
          </button>
        </div>
      </>
    );
  };
  function Url() {
    return (
      <>
        <UrlUI
          src
          srcMode
          onSrcChange={onSrcChange}
          onSrcModeChange={onSrcModeChange}
          opened
          setOpened={setOpened}
        />
      </>
    );
  }

  const tabs = [
    {
      label: "Upload",
      key: "upload",
      children: Upload(),
    },
    {
      label: "Library",
      key: "library",
      children: Library(),
    },
    {
      label: "URL",
      key: "url",
      children: Url(),
    },
  ];

  return (
    <div>
      <Modal
        isOpened={opened}
        onOutsideClick={() => {
          setOpened(false);
        }}
      >
        <div className="rounded-[21px] bg-white grigora-video-picker flex flex-col mw-[80vw] mh-[80vh] w-[50vw] h-[60vh] p-5 cursor-default">
          <div className="modal-heading flex items-center justify-between">
            Choose an Video
            <span
              onClick={(e) => {
                e.stopPropagation();
                setOpened(false);
              }}
              className="p-2 grid cursor-pointer text-black hover:text-gray-400"
            >
              <FontAwesomeIcon icon={faClose} />
            </span>
          </div>
          <Tabs
            initialOpenIndex={
              srcMode === "cdn" ? 1 : srcMode === "url" ? 2 : undefined
            }
            items={tabs}
          />
        </div>
      </Modal>
    </div>
  );
}

export default VideoPicker;
